import { useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { PersonaIcon } from './PersonaIcon'
import { Shard } from '../types/shard'
import { Worker } from '../hooks/useWorkers'

interface ScratchpadPanelProps {
  workerName: string | null
  workers: Worker[]
  shards: Shard[]
  onClose: () => void
  onShardClick: (shard: Shard) => void
}

export function ScratchpadPanel({ workerName, workers, shards, onClose, onShardClick }: ScratchpadPanelProps) {
  const worker = workers.find(w => w.name === workerName)

  // Find the shard(s) assigned to this worker with scratchpad content
  const workerShards = useMemo(() => {
    if (!workerName) return []
    return shards.filter(s =>
      (s.assigned_worker === workerName || s.assignee === workerName) && s.scratchpad
    )
  }, [workerName, shards])

  return (
    <AnimatePresence>
      {workerName && (
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 360, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 35 }}
          className="border-l border-cosmic-border bg-cosmic-abyss overflow-hidden flex-shrink-0"
        >
          <div className="w-[360px] h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-cosmic-border">
              <div className="flex items-center gap-2">
                {worker && <PersonaIcon persona={worker.persona} size="sm" />}
                <div>
                  <div className="text-sm font-medium text-white">{workerName}</div>
                  {worker && (
                    <div className="flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${worker.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      <span className="text-xs text-gray-500">{worker.status}</span>
                    </div>
                  )}
                </div>
              </div>
              <button onClick={onClose} className="text-gray-500 hover:text-white text-lg">&times;</button>
            </div>

            {/* Scratchpad content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {workerShards.length > 0 ? (
                workerShards.map(shard => (
                  <div key={shard.id} className="space-y-2">
                    <button
                      onClick={() => onShardClick(shard)}
                      className="text-xs font-mono text-cosmic-nebula hover:underline"
                    >
                      {shard.id}: {shard.title}
                    </button>
                    <div className="themed-panel border border-cosmic-border rounded-lg p-3 text-sm">
                      <div className="prose prose-invert prose-sm max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {shard.scratchpad || ''}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-600 text-sm text-center py-8">
                  No scratchpad data for this worker
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
